import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { fetchLeaderboardData } from './db/dbLeaderboard.js';
import { fetchMythicsData } from './db/dbMythics.js';
import { getCachedMythicsData, setCachedMythicsData } from './db/mythicsCache.js';
import getConversionRates from './api/coingecko.js';
import { useWallet } from './WalletContext.js';
import './styles/HolderProfile.css';

const LEADERBOARD_CACHE_KEY = 'leaderboardData';
const LEADERBOARD_CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function getCachedLeaderboard() {
  const raw = localStorage.getItem(LEADERBOARD_CACHE_KEY);
  if (!raw) return null;
  try {
    const { data, ts } = JSON.parse(raw);
    if (Date.now() - ts > LEADERBOARD_CACHE_TTL_MS) return null;
    return data;
  } catch {
    return null;
  }
}

function setCachedLeaderboard(data) {
  localStorage.setItem(LEADERBOARD_CACHE_KEY, JSON.stringify({ data, ts: Date.now() }));
}

const isWallet = (value) => /^0x[a-fA-F0-9]{40}$/.test(value || '');

function parseFloorString(floor) {
  if (!floor || floor === '-') return null;
  const [value, currency] = String(floor).replace(/,/g, '').trim().split(/\s+/);
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || !currency) return null;
  return { value: numeric, currency: currency.toUpperCase() };
}

function HolderProfile() {
  const { walletAddress } = useParams();
  const { walletAddress: connectedWallet } = useWallet();
  const normalizedWallet = (walletAddress || '').toLowerCase();
  const normalizedConnected = (connectedWallet || '').toLowerCase();
  const isOwnProfile = Boolean(
    normalizedWallet && normalizedConnected && normalizedWallet === normalizedConnected
  );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [summary, setSummary] = useState(null);
  const [rows, setRows] = useState([]);
  const [sortBy, setSortBy] = useState({ id: 'rank', desc: true });
  const [showOwnedOnly, setShowOwnedOnly] = useState(false);
  const [copiedWallet, setCopiedWallet] = useState(false);

  const handleCopyWallet = () => {
    navigator.clipboard.writeText(walletAddress || '').catch(() => {});
    setCopiedWallet(true);
    setTimeout(() => setCopiedWallet(false), 1500);
  };

  useEffect(() => {
    const load = async () => {
      if (!isWallet(walletAddress)) {
        setError('Invalid wallet address format.');
        setLoading(false);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        let leaderboard = getCachedLeaderboard();
        if (!leaderboard) {
          leaderboard = await fetchLeaderboardData();
          setCachedLeaderboard(leaderboard);
        }

        let mythics = getCachedMythicsData();
        if (!mythics) {
          mythics = await fetchMythicsData();
          setCachedMythicsData(mythics);
        }
        const rates = await getConversionRates();

        const ownerStats = leaderboard?.ownerStatisticsArray || [];
        const ownerEntry = ownerStats.find(
          (o) => (o.owner || '').toLowerCase() === normalizedWallet
        );

        const rankedByPower = [...ownerStats].sort((a, b) => (b.points || 0) - (a.points || 0));
        const position = rankedByPower.findIndex(
          (o) => (o.owner || '').toLowerCase() === normalizedWallet
        );

        const nfts = mythics?.nfts || [];
        const collectionRows = nfts.map((nft) => {
          const owners = Array.isArray(nft.owners) ? nft.owners : [];
          const ownedCount = owners.filter((o) => (o || '').toLowerCase() === normalizedWallet).length;
          return {
            ...nft,
            ownedCount,
            isOwned: ownedCount > 0,
          };
        });

        const computedTotalOwned = collectionRows.reduce((sum, row) => sum + row.ownedCount, 0);
        const computedUnique = collectionRows.filter((row) => row.ownedCount > 0).length;
        const computedTotalRank = collectionRows.reduce(
          (sum, row) => sum + ((Number(row.rank) || 0) * row.ownedCount),
          0
        );
        const portfolioByCurrency = collectionRows.reduce((acc, row) => {
          if (!row.ownedCount) return acc;
          const parsed = parseFloorString(row.floor);
          if (!parsed) return acc;
          const current = acc[parsed.currency] || 0;
          acc[parsed.currency] = current + (parsed.value * row.ownedCount);
          return acc;
        }, {});
        const portfolioEstimate = Object.entries(portfolioByCurrency)
          .filter(([, v]) => Number.isFinite(v) && v > 0)
          .sort((a, b) => b[1] - a[1])
          .map(([currency, value]) => `${value % 1 === 0 ? value.toFixed(0) : value.toFixed(2)} ${currency}`)
          .join(' + ');
        const usdByCurrency = {
          ETH: rates?.ethereum?.usd || 0,
          WETH: rates?.ethereum?.usd || 0,
          POL: rates?.['matic-network']?.usd || 0,
          MATIC: rates?.['matic-network']?.usd || 0,
          USDC: rates?.['usd-coin']?.usd || 1,
          USD: 1,
        };
        const portfolioEstimateUsd = Object.entries(portfolioByCurrency).reduce((sum, [currency, value]) => {
          const rate = usdByCurrency[currency] || 0;
          return sum + (value * rate);
        }, 0);

        setSummary({
          wallet: walletAddress,
          username: ownerEntry?.username || null,
          position: position >= 0 ? position + 1 : null,
          totalNFTs: ownerEntry?.totalNFTs ?? computedTotalOwned,
          uniqueNFTs: ownerEntry?.uniqueNFTs ?? computedUnique,
          points: ownerEntry?.points ?? 0,
          highestRank: ownerEntry?.highestRank ?? null,
          totalRank: ownerEntry?.totalRank ?? computedTotalRank,
          portfolioEstimate: portfolioEstimate || '—',
          portfolioEstimateUsd: Number.isFinite(portfolioEstimateUsd) && portfolioEstimateUsd > 0
            ? portfolioEstimateUsd
            : null,
        });
        setRows(collectionRows);
      } catch (err) {
        if (err?.code === 'permission-denied') {
          setError('Database permissions not configured yet. Update Firestore security rules and run the cron job from the admin panel.');
        } else {
          setError('Failed to load holder profile. Please try again.');
        }
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [walletAddress, normalizedWallet]);

  const filteredRows = useMemo(() => {
    const baseRows = showOwnedOnly ? rows.filter((row) => row.ownedCount > 0) : rows;
    const sorted = [...baseRows].sort((a, b) => {
      const dir = sortBy.desc ? -1 : 1;
      if (sortBy.id === 'ownedCount' || sortBy.id === 'rank' || sortBy.id === 'minted') {
        return ((a[sortBy.id] || 0) - (b[sortBy.id] || 0)) * dir;
      }
      if (sortBy.id === 'name') {
        return (a.name || '').localeCompare(b.name || '') * dir;
      }
      return 0;
    });
    return sorted;
  }, [rows, sortBy, showOwnedOnly]);

  const onSort = (id) => {
    setSortBy((prev) => ({
      id,
      desc: prev.id === id ? !prev.desc : true,
    }));
  };

  const indicator = (id) => (sortBy.id === id ? (sortBy.desc ? ' ▼' : ' ▲') : '');

  if (loading) {
    return (
      <div className="holder-profile-page">
        <div className="holder-loading loading">Loading holder profile...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-message">
        <div className="error-message-card">
          <p>{error}</p>
          <button onClick={() => window.location.reload()}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className={`holder-profile-page${isOwnProfile ? ' holder-profile-page--self' : ''}`}>
      <div className="holder-header">
        <p className="holder-back-link"><Link to="/leaderboard">← Back to Leaderboard</Link></p>
        <h2>Mythic Holder</h2>
        <p className="holder-wallet">
          {summary?.username ? `${summary.username} · ` : ''}
          <code>{summary?.wallet}</code>
          <button
            className="holder-copy-btn"
            onClick={handleCopyWallet}
            aria-label="Copy wallet address"
            title="Copy wallet address"
          >
            {copiedWallet ? '✓' : '⧉'}
          </button>
        </p>
      </div>

      <div className="holder-summary-grid">
        <div className="holder-summary-card"><span>Position</span><strong>{summary?.position ?? '—'}</strong></div>
        <div className="holder-summary-card"><span>Power</span><strong>{summary?.points ?? 0}</strong></div>
        <div className="holder-summary-card"><span>Total Rank</span><strong>{summary?.totalRank ?? 0}</strong></div>
        <div className="holder-summary-card"><span>Total Mythics</span><strong>{summary?.totalNFTs ?? 0}</strong></div>
        <div className="holder-summary-card"><span>Unique Mythics</span><strong>{summary?.uniqueNFTs ?? 0}</strong></div>
        <div className="holder-summary-card"><span>Highest Rank</span><strong>{summary?.highestRank ?? '—'}</strong></div>
        <div className="holder-summary-card holder-summary-card-wide">
          <span>Portfolio Estimate (by floor)</span>
          <strong>{summary?.portfolioEstimate ?? '—'}</strong>
          {summary?.portfolioEstimateUsd && (
            <em className="holder-summary-subvalue">
              ≈ ${summary.portfolioEstimateUsd.toLocaleString(undefined, { maximumFractionDigits: 0 })}
            </em>
          )}
        </div>
      </div>

      <div className="holder-table-wrap">
        <div className="holder-collection-header">
          <div>
            <h3 className="holder-collection-title">Collection</h3>
            <p className="holder-collection-sub">
              {showOwnedOnly ? 'Showing only owned mythics' : 'Showing full mythics collection'}
            </p>
          </div>
          <label className="holder-owned-toggle">
            <input
              type="checkbox"
              checked={showOwnedOnly}
              onChange={(e) => setShowOwnedOnly(e.target.checked)}
            />
            Owned only
          </label>
        </div>

        <table className="holder-table">
          <thead>
            <tr>
              <th onClick={() => onSort('rank')}>Rank{indicator('rank')}</th>
              <th></th>
              <th onClick={() => onSort('name')}>Name{indicator('name')}</th>
              <th onClick={() => onSort('ownedCount')}>Owned{indicator('ownedCount')}</th>
              <th onClick={() => onSort('minted')}>Minted{indicator('minted')}</th>
              <th>Floor</th>
            </tr>
          </thead>
          <tbody>
            {filteredRows.length === 0 ? (
              <tr>
                <td colSpan={6} className="holder-empty">
                  {showOwnedOnly ? 'No owned mythics found for this wallet.' : 'No mythics available.'}
                </td>
              </tr>
            ) : filteredRows.map((row) => (
              <tr key={row.rank} className={row.isOwned ? 'holder-owned-row' : 'holder-not-owned-row'}>
                <td>{row.rank}</td>
                <td className="holder-image-cell">
                  <Link to={`/mythics/${row.rank}`}>
                    <img src={row.image_url} alt={row.name} loading="lazy" />
                  </Link>
                </td>
                <td className="holder-name-cell">
                  <Link to={`/mythics/${row.rank}`}>{row.name}</Link>
                </td>
                <td>{row.ownedCount > 0 ? row.ownedCount : '-'}</td>
                <td>{row.minted ?? '-'}</td>
                <td>{row.floor || '-'}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default HolderProfile;
